import * as React from 'react';
import styled from 'styled-components';

import Logo from './Logo';
import MainMenu from './MainMenu';
import Actions from './Actions';
import { PageWidth } from '../Layouts';
import { COLORS, BREAKPOINTS } from '../styles';
import RightSideNav from './RightSideNav';
import { NavOption, NavButton, ActionOptions, UserMenuItem } from '../types';
import MobileMenu from './mobile/MobileMenu';
import { Menu } from '../icons';
import UserMenu from './UserMenu/UserMenu';
import useClickOutside from '../hooks/useClickOutside';
import { Menu as MobileMenuWrapper } from './mobile/CommonStyles';

const HeaderWrapper = styled.header`
  background: white;
  border-bottom: 1px solid ${COLORS.neutral_200};

  height: 48px;

  ${BREAKPOINTS.SMALL} {
    height: 56px;
  }
  ${BREAKPOINTS.MEDIUM} {
    height: 64px;
  }

  @media all and (-ms-high-contrast: none) {
    height: auto;
  }

  @media print {
    border-bottom: none;
  }
`;

const RowLayout = styled.div`
  display: flex;
`;

const RightSide = styled.div`
  display: flex;
  margin: 0 0 0 auto;
`;

const MenuWrapper = styled.div<MenuWrapperProps>`
  background: rgba(0, 0, 0, 0.5);
  display: ${props => (props.isVisible ? 'flex' : 'none')};
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  overflow-y: hidden;
  bottom: 0;
  right: 0;
  z-index: 1;
`;

const UserMenuWrapper = styled.div<UserMenuWrapperProps>`
  background-color: ${COLORS.white};
  display: flex;
  margin-left: ${props => (props.floatRight ? 'auto' : 'unset')};
  position: absolute;
  right: ${props => (props.offsetRight ? `${window.innerWidth - props.offsetRight + 296}px` : '10%')};
  top: 56px;

  &:focus-within,
  &:hover,
  &.open {
    background: ${COLORS.white};
  }
`;

interface UserMenuWrapperProps {
  floatRight: boolean;
  offsetRight: number | null | undefined;
}

const HamburgerButton = styled.button`
  border-width: 0;
  background-color: ${COLORS.white};
  height: 48px;

  ${BREAKPOINTS.SMALL} {
    height: 56px;
  }
  ${BREAKPOINTS.MEDIUM} {
    height: 64px;
  }
  &:hover {
    cursor: pointer;
  }

  ${BREAKPOINTS.MEDIUM} {
    display: none;
  }
`;

interface MenuWrapperProps {
  isVisible: boolean;
}

type Props = {
  name?: string;
  mainNavigationOptions?: NavOption[];
  secondaryNavigationOptions?: NavOption[];
  secondaryButton?: NavButton;
  actions?: ActionOptions;
  firstName?: string;
  lastName?: string;
  email?: string;
  organizationName?: string;
  signout?: (e: any) => void;
  isAuthenticated?: boolean;
  mobileActions?: ActionOptions;
  notifications?: NavOption[];
  switcherAction?: () => void;
  reverseRightSideOrder?: boolean;
  showBetaTag?: boolean;
  accountSection?: UserMenuItem[];
  organizationSection?: UserMenuItem[];
  supportSection?: UserMenuItem[];
  accountMenuHeader?: string;
  signOutLabel?: string;
  useUserMenu?: boolean;
  trackLogoClick?: () => void;
  mainLogoTo?: string;
  testId?: string;
};

/**
 * @param props
 * @constructor
 * @description This component is a global navigation header bar
 */
const GlobalNavigationBar = ({
  name,
  mainNavigationOptions,
  secondaryNavigationOptions,
  secondaryButton,
  actions,
  firstName,
  lastName,
  email,
  organizationName,
  signout,
  isAuthenticated,
  mobileActions,
  notifications,
  switcherAction,
  reverseRightSideOrder = false,
  showBetaTag,
  accountSection,
  organizationSection,
  supportSection,
  accountMenuHeader,
  signOutLabel,
  useUserMenu,
  mainLogoTo,
  trackLogoClick,
  testId,
}: Props): React.ReactElement<Props> => {
  const [showMenu, setShowMenu] = React.useState(false);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [showMobileUserMenu, setShowMobileUserMenu] = React.useState<boolean>(false);
  const menuRef = React.useRef(null);
  const userMenuRef = React.useRef<HTMLDivElement>(null);
  const rightSideRef = React.useRef(null);
  const [avatarRight, setAvatarRight] = React.useState<number>(0);

  const handleClickMenuAction = () => {
    setShowMenu(false);
  };

  useClickOutside(userMenuRef, () => (showUserMenu ? setShowUserMenu(false) : null));
  useClickOutside(menuRef, () => {
    if (showMenu) {
      setShowMobileUserMenu(false);
      setShowMenu(false);
    }
  });

  React.useEffect(() => {
    const handleResize = () => {
      const right = document?.getElementById('avatarContainer')?.getBoundingClientRect()?.right;
      if (right) {
        setAvatarRight(right);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.getElementById('avatarContainer')?.getBoundingClientRect()?.right]);

  return (
    <>
      <HeaderWrapper data-testid={testId}>
        <PageWidth>
          <RowLayout>
            <Logo name={name || ''} showBetaTag={showBetaTag} onClick={trackLogoClick} to={mainLogoTo} />
            {isAuthenticated && <MainMenu navigationOptions={mainNavigationOptions || []} rightSideRef={rightSideRef} />}
            <RightSide className="GlobalNavigationRightSide" ref={rightSideRef}>
              {reverseRightSideOrder ? (
                <>
                  <Actions
                    actions={actions}
                    firstName={firstName || ''}
                    lastName={lastName || ''}
                    hideOnLowWidth={true}
                    useUserMenu={useUserMenu}
                    setShowUserMenu={setShowUserMenu}
                  />
                  <RightSideNav navigationOptions={secondaryNavigationOptions} secondaryButton={secondaryButton} />
                </>
              ) : (
                <>
                  <RightSideNav navigationOptions={secondaryNavigationOptions} secondaryButton={secondaryButton} />
                  <Actions
                    actions={actions}
                    firstName={firstName || ''}
                    lastName={lastName || ''}
                    hideOnLowWidth={true}
                    useUserMenu={useUserMenu}
                    setShowUserMenu={setShowUserMenu}
                  />
                </>
              )}
              <HamburgerButton onClick={() => setShowMenu(!showMenu)} hidden={!isAuthenticated}>
                <Menu size="24px" />
              </HamburgerButton>
            </RightSide>
          </RowLayout>
        </PageWidth>
      </HeaderWrapper>
      <MenuWrapper className={showMenu ? 'open' : 'closed'} isVisible={showMenu}>
        <MobileMenuWrapper ref={menuRef} onClick={() => {}} role="menu" aria-labelledby="UserMenuButton">
          <MobileMenu
            name={name || ''}
            clickMenuAction={() => {
              setShowMobileUserMenu(false);
              handleClickMenuAction();
            }}
            navigationOptions={mainNavigationOptions || []}
            secondaryNavigationOptions={secondaryNavigationOptions}
            secondaryButton={secondaryButton}
            firstName={firstName || ''}
            lastName={lastName || ''}
            email={email || ''}
            signout={signout}
            notifications={notifications}
            actions={mobileActions}
            switcherAction={switcherAction}
            isAuthenticated={isAuthenticated}
            accountSection={accountSection || []}
            organizationSection={organizationSection || []}
            supportSection={supportSection || []}
            accountMenuHeader={accountMenuHeader || ''}
            signOutLabel={signOutLabel || ''}
            organizationName={organizationName || ''}
            useUserMenu={useUserMenu}
            showUserMenu={showMobileUserMenu}
            setShowUserMenu={setShowMobileUserMenu}
          />
        </MobileMenuWrapper>
      </MenuWrapper>
      {useUserMenu && (
        <UserMenuWrapper ref={userMenuRef} className={showUserMenu ? 'open' : 'closed'} floatRight={false} offsetRight={avatarRight} data-testid="testUserMenuWrapper">
          <UserMenu
            firstName={firstName || ''}
            lastName={lastName || ''}
            email={email || ''}
            organizationName={organizationName || ''}
            clickMenuAction={() => setShowUserMenu(false)}
            accountSection={accountSection}
            organizationSection={organizationSection}
            supportSection={supportSection}
            signOut={signout}
            signOutLabel={signOutLabel || ''}
          />
        </UserMenuWrapper>
      )}
    </>
  );
};

export default GlobalNavigationBar;
